/* eslint-disable react/prop-types */ // Props are validated via TypeScript interfaces
import React, { type CSSProperties, type ReactNode, useRef, useEffect, useState } from 'react';
import classNames from 'classnames';

import type { RingStyle } from './ring_styles';

interface DemographicsMapProps {
  children?: ReactNode;
  className?: string;
  distanceUnit?: 'mi' | 'km';
  latitude: number;
  longitude: number;
  pin?: () => HTMLElement;
  radiusRings?: (number | null)[];
  // sizeRank is the ring's position from smallest to largest, 0 for the smallest.
  ringStyle: (index: number, sizeRank: number) => RingStyle;
  style?: CSSProperties;
}

const DemographicsMap: React.FC<DemographicsMapProps> = ({
  children,
  className,
  distanceUnit = 'mi',
  latitude,
  longitude,
  pin,
  radiusRings = [],
  ringStyle,
  style
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const circlesRef = useRef<google.maps.Circle[]>([]);

  // Helper functions
  const toMeters = (radius: number) => radius * (distanceUnit === 'km' ? 1000 : 1609.34);

  // Validate lat/lng coordinates
  const isValidCoordinate = (lat: number, lng: number): boolean => {
    return !isNaN(lat) && !isNaN(lng) &&
           lat !== null && lat !== undefined &&
           lng !== null && lng !== undefined &&
           lat >= -90 && lat <= 90 &&
           lng >= -180 && lng <= 180;
  };

  if (!isValidCoordinate(latitude, longitude)) {
    return (
      <div className="demographics-map-wrapper" style={{ position: 'relative' }}>
        <div
          className="demographics-map-error"
          style={{
            width: '100%',
            height: '560px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '16px',
            textAlign: 'center'
          }}
        >
          <div>
            <div style={{ marginBottom: '8px', fontSize: '18px' }}>📍</div>
            <div>Invalid coordinates provided</div>
          </div>
        </div>
      </div>
    );
  }

  // Initialize map when component mounts
  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const mapOptions = {
      zoom: 14,
      minZoom: 4,
      streetViewControl: false,
      mapTypeControl: true,
      tilt: 0,
      center: {
        lat: latitude,
        lng: longitude
      },
      gestureHandling: 'cooperative',
      mapId: 'demographics-map'
    };

    // Create map
    const newMap = new google.maps.Map(mapRef.current, mapOptions);
    setMap(newMap);

    // Create marker
    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      map: newMap,
      position: mapOptions.center,
      gmpDraggable: false,
      ...(pin && { content: pin() })
    });
    setMarker(newMarker);

    return () => {
      // Cleanup circles on component unmount
      if (circlesRef.current) {
        circlesRef.current.forEach(circle => circle.setMap(null));
        circlesRef.current = [];
      }
    };
  }, []);

  // Update marker position and circles when lat/lng or radiusRings change
  useEffect(() => {
    if (!map || !marker) return;

    const position = {
      lat: latitude,
      lng: longitude
    };

    marker.position = position;
    map.setCenter(position);

    // Clear any existing circles
    if (circlesRef.current) {
      circlesRef.current.forEach(circle => circle.setMap(null));
      circlesRef.current = [];
    }

    // Only draw circles for filled rings; freshly added (null) rings have no
    // value yet and should not render a 0-radius circle.
    const filledRadii = radiusRings.filter((r): r is number => typeof r === 'number' && r > 0);

    if (filledRadii.length > 0) {
      const radiiBySize = [...filledRadii].sort((a, b) => a - b);

      circlesRef.current = filledRadii.map((radius, index) => new google.maps.Circle({
        map,
        center: position,
        radius: toMeters(radius),
        ...ringStyle(index, radiiBySize.indexOf(radius)),
        clickable: false
      }));

      // Fix zoom so that we can fit all circles in the viewport on first load
      const bounds = new google.maps.LatLngBounds();
      const largestRadius = Math.max(...filledRadii);

      // Make sure the geometry library is loaded before using these functions
      if (google.maps.geometry && google.maps.geometry.spherical) {
        const northEast = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(position.lat, position.lng),
          toMeters(largestRadius) * 1.1,
          45
        );
        const southWest = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(position.lat, position.lng),
          toMeters(largestRadius) * 1.1,
          225
        );
        bounds.extend(northEast);
        bounds.extend(southWest);
        map.fitBounds(bounds);
      } else {
        map.setZoom(Math.max(10, 14 - Math.floor(largestRadius / 2)));
      }
    }
  }, [latitude, longitude, radiusRings, distanceUnit, map, marker]);

  return (
    <div className="demographics-map-wrapper" style={{ position: 'relative' }}>
      <div className={classNames('demographics-map', className)} ref={mapRef} style={style} />
      {children}
    </div>
  );
};

export default DemographicsMap;
